import React, { useEffect, useRef } from 'react';
import { SnackbarProvider } from 'notistack';
import { usePageView, useUsermaven } from '@usermaven/react';
import { ToastProvider } from './context/ToastContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { HelpHubProvider } from './context/HelpHubContext';
import HelpHubCore from './components/HelpHubCore';
import CreateAgencyPage from './pages/CreateAgencyPage';


// Layouts
import { AppShell } from './layouts';

// Pages
import {
  WorkspacesPage,
  SubscriptionPage,
  CheckoutPage,
  BillingPage,
  CustomDomainsPage,
  IPRestrictionsPage,
  SmtpGatewayPage,
  ProfilePage,
  AgencyPage,
  AuditLogsPage,
  DataConnectionsPage,
} from './pages';

import FrillSSOPage from './pages/FrillSSOPage';
import SelectAgencyPage from './pages/SelectAgencyPage';

// Auth Components
import Auth from './components/Auth';
import MagicLinkHandler from './components/MagicLinkHandler';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AcceptInvite from './components/AcceptInvite';
import TransferAcceptPage from './pages/TransferAcceptPage';

// Other Components
import TwilioComplianceMockup from './components/TwilioComplianceMockup';
import RedeemLifetimeDeal from './components/RedeemLifetimeDeal';

import { useParams } from 'react-router-dom';

const WorkspaceBillingRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/dashboard/billing?tab=history&workspace_id=${id}`} replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--v2-bg)'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--v2-border)',
          borderTopColor: 'var(--v2-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/auth" replace />;
};

// ============================================
// USERMAVEN — guard wrappers
// These inner components call hooks and must only ever mount
// when <UsermavenProvider> is present in the tree (set via
// window.__dfxAnalyticsReady in index.tsx). The outer components
// check that flag first so the hooks never fire without a provider.
// ============================================

const PageViewTrackerInner: React.FC = () => {
  usePageView();
  return null;
};
const PageViewTracker: React.FC = () => {
  if (!(window as any).__dfxAnalyticsReady) return null;
  return <PageViewTrackerInner />;
};

const UsermavenIdentifyInner: React.FC = () => {
  const { user, agencyId, agencyName } = useAuth();
  const { id } = useUsermaven();
  const identified = useRef<number | null>(null);
  useEffect(() => {
    if (user && user.id !== identified.current) {
      identified.current = user.id;
      const nameParts = (user.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      id({
        id: String(user.id),
        email: user.email,
        first_name: firstName,
        last_name: lastName,
        ...(agencyId && agencyName ? {
          company: {
            id: agencyId,
            name: agencyName,
          }
        } : {}),
      });
    }
    if (!user) {
      identified.current = null;
    }
  }, [user, id, agencyId, agencyName]);
  return null;
};
const UsermavenIdentify: React.FC = () => {
  if (!(window as any).__dfxAnalyticsReady) return null;
  return <UsermavenIdentifyInner />;
};

// ============================================
// APP CONTENT
// ============================================

const AppContent: React.FC = () => {
  const { user } = useAuth();
  return (
    <HelpHubProvider
      mode={user ? "authenticated" : "guest"}
      authContext={{ user, serverRegion: process.env.REACT_APP_REGION || 'US' }}
    >
      <Router>
        <PageViewTracker />
        <Routes>
          {/* Public routes */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/magic" element={<MagicLinkHandler />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/transfers/accept/:token" element={<TransferAcceptPage />} />
          <Route path="/select-agency" element={<SelectAgencyPage />} />
          <Route path="/twilio-compliance" element={<TwilioComplianceMockup />} />
          <Route path="/frill-sso" element={<FrillSSOPage />} />

          {/* Lifetime deal redemption - works logged in or out */}
          <Route path="/redeem" element={<RedeemLifetimeDeal />} />

          {/* Protected routes with AppShell layout */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* Workspaces */}
            <Route index element={<WorkspacesPage />} />
            <Route path="workspace/:id/subscription" element={<SubscriptionPage />} />
            <Route path="workspace/:id/checkout" element={<CheckoutPage />} />
            <Route path="workspace/:id/domains" element={<CustomDomainsPage />} />
            <Route path="workspace/:id/ip-restrictions" element={<IPRestrictionsPage />} />
            <Route path="workspace/:id/smtp" element={<SmtpGatewayPage />} />
            <Route path="workspace/:id/data-connections" element={<DataConnectionsPage />} />
            <Route path="workspace/:id/billing-history" element={<WorkspaceBillingRedirect />} />

            {/* Billing */}
            <Route path="billing" element={<BillingPage />} />

            {/* Settings */}
	    <Route path="create-agency" element={<CreateAgencyPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="agency" element={<AgencyPage />} />
            <Route path="audit-logs" element={<AuditLogsPage />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Catch all - redirect to auth */}
          <Route path="*" element={<Navigate to="/auth" replace />} />
        </Routes>
      </Router>
      <HelpHubCore />
      <UsermavenIdentify />
    </HelpHubProvider>
  );
};

function App() {
  return (
    <SnackbarProvider maxSnack={3}>
      <AuthProvider>
        <SubscriptionProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </SnackbarProvider>
  );
}

export default App;
